import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { api, track } from "@/lib/api";
import { Seo } from "@/components/Seo";

const STEPS = ["Property type", "Surface type", "Requirement"];

export default function Solutions() {
  const [svc, setSvc] = useState(null);
  const [step, setStep] = useState(0);
  const [sel, setSel] = useState({ property_type: "", surface_type: "", requirement: "" });
  const [recs, setRecs] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    track("page_view", { page: "solutions" });
    api.get("/services").then(({ data }) => setSvc(data[0])).catch(() => {});
  }, []);

  const pick = (key, val) => {
    setSel((s) => ({ ...s, [key]: val }));
    if (step < 2) setStep(step + 1);
    else fetchRecs({ ...sel, [key]: val });
  };

  const fetchRecs = async (s) => {
    setLoading(true);
    try {
      const { data } = await api.post("/recommend", { surface_type: s.surface_type, property_type: s.property_type, requirement: s.requirement });
      setRecs(data.recommendations);
    } finally { setLoading(false); }
  };

  const options = svc ? [svc.property_types, svc.surface_types, svc.requirements][step] : [];
  const keys = ["property_type", "surface_type", "requirement"];

  return (
    <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-16" data-testid="solutions-page">
      <Seo title="Find Your Protection" description="Answer three questions and ERRAH recommends the right surface protection." />
      <p className="errah-label gold-text mb-4">Find your protection</p>
      <h1 className="font-display text-4xl lg:text-6xl font-light tracking-tighter">Tell us about your space.</h1>

      {!recs && (
        <div className="mt-12 max-w-3xl">
          <div className="flex gap-2 mb-8">
            {STEPS.map((s, i) => (
              <div key={s} className="flex-1">
                <div className={`h-1 ${i <= step ? "bg-[#C9A45C]" : "bg-white/10"}`} />
                <p className={`errah-label mt-2 ${i === step ? "gold-text" : ""}`}>{s}</p>
              </div>
            ))}
          </div>
          <h2 className="font-display text-2xl mb-6">{STEPS[step]}</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {options.map((o) => (
              <button key={o} onClick={() => pick(keys[step], o)} data-testid={`opt-${o.replace(/\s/g, "-")}`}
                className="hair-border bg-white/[0.02] px-5 py-5 text-left text-sm hover:border-[#C9A45C] hover:bg-white/[0.04] transition-colors">
                {o}
              </button>
            ))}
          </div>
          {step > 0 && <button onClick={() => setStep(step - 1)} className="mt-8 text-sm text-gray-400 hover:text-white">← Back</button>}
        </div>
      )}

      {loading && <div className="mt-16 flex justify-center"><div className="w-6 h-6 border-2 border-[#C9A45C] border-t-transparent rounded-full animate-spin" /></div>}

      {recs && !loading && (
        <div className="mt-12" data-testid="solutions-results">
          <div className="flex flex-wrap gap-2 mb-8">
            {Object.entries(sel).map(([k, v]) => v && <span key={k} className="px-3 py-1 text-xs border border-[#C9A45C]/40 text-[#C9A45C]">{v}</span>)}
          </div>
          <h2 className="font-display text-3xl mb-6">Recommended for you</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {recs.map((p) => (
              <button key={p.id} onClick={() => navigate(`/products/${p.id}`)} data-testid={`rec-${p.id}`}
                className="text-left group hair-border bg-white/[0.02] hover:bg-white/[0.04] transition-colors">
                <div className="aspect-square overflow-hidden"><img src={p.image} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" /></div>
                <div className="p-4"><h3 className="font-display text-base">{p.name}</h3>{p.price_from && <p className="mt-2 text-sm gold-text">From ₹{p.price_from}</p>}</div>
              </button>
            ))}
          </div>
          <button onClick={() => navigate("/quote", { state: { surface_type: sel.surface_type, property_type: sel.property_type, preferred_service: sel.requirement } })}
            data-testid="solutions-quote-cta" className="mt-10 inline-flex items-center gap-2 bg-[#C9A45C] text-black px-7 py-3.5 text-sm font-semibold hover:bg-[#E4C987] transition-colors">
            Request a Quote <ArrowRight size={16} />
          </button>
          <button onClick={() => { setRecs(null); setStep(0); setSel({ property_type: "", surface_type: "", requirement: "" }); }} className="mt-4 ml-4 text-sm text-gray-400 hover:text-white">Start over</button>
        </div>
      )}
    </div>
  );
}
